import './Hero.scss';

import { type FC, useMemo } from 'react';
import classNames from 'classnames';
import { some, max } from 'lodash-es';

import groupBy from 'utils/groupBy';

import * as Core from 'components/core';
import ColorBar from 'components/ColorBar';
import TrialFontsButton from './TrialFontsButton';
import { useTranslation } from 'react-i18next';

interface Props {
  family: {
    conceptFont: boolean;
  };
  fontFaces: {
    id: number;
    description: string;
    width: { slug: string; name: string; position: number };
    weight: { slug: string; name: string; position: number };
    style: { slug: string; name: string; position: number };
    familyPreview: {
      url: string | null;
      width: number | null;
      height: number | null;
    };
  }[];
}

const FamiliesHero: FC<Props> = (props) => {
  const { t } = useTranslation();

  const { family, fontFaces } = props;

  // Break families with only one style (e.g. no italics), but more than 10
  // faces into two columns.
  const singleStyle = useMemo(
    () =>
      fontFaces.length > 10 &&
      !some(fontFaces, (face) => face.style.slug === 'italic'),
    [fontFaces],
  );

  const imageRatio = useMemo(() => {
    const maxWidth = max(fontFaces.map((face) => face.familyPreview?.width));
    const maxHeight = max(fontFaces.map((face) => face.familyPreview?.height));

    return maxWidth != null && maxHeight != null
      ? Math.min(maxHeight / maxWidth, 0.08)
      : 0.08;
  }, [fontFaces]);

  return (
    <ColorBar>
      <style>
        {`
          @media screen and (max-width: 599px) and (max-width: 1400px) {
            .FamiliesHero-specimen-pair-style-image {
              width: auto;
              height: ${imageRatio * 0.9 * 100}vw;
            }
          }

          @media screen and (min-width: 600px) {
            .FamiliesHero-specimen-pair-style-image {
              width: auto;
              height: ${imageRatio * 0.45 * 100}vw;
            }
          }

          @media screen and (min-width: 1401px) {
            .FamiliesHero-specimen-pair-style-image {
              width: auto;
              height: ${imageRatio * 0.45 * 1400}px;
            }
          }
        `}
      </style>

      <div className="FamiliesHero content-wrapper">
        <div
          className={classNames('FamiliesHero-specimen', {
            'is-singleStyle': singleStyle,
          })}
        >
          {groupBy(fontFaces, (face) => face.width.slug).map((result) => (
            <div key={result.name} className="FamiliesHero-specimen-width">
              {groupBy(result.elements, (face) => face.weight.slug).map(
                (result) => (
                  <div key={result.name} className="FamiliesHero-specimen-pair">
                    {result.elements.map((face) => (
                      <div
                        key={face.id}
                        className="FamiliesHero-specimen-pair-style"
                      >
                        <img
                          src={face.familyPreview?.url ?? undefined}
                          width={face.familyPreview?.width ?? undefined}
                          height={face.familyPreview?.height ?? undefined}
                          alt={face.description}
                          className="FamiliesHero-specimen-pair-style-image"
                        />
                      </div>
                    ))}
                  </div>
                ),
              )}
            </div>
          ))}
        </div>

        {!family.conceptFont && (
          <div className="FamiliesHero-buttons">
            <Core.Link to="#purchase" button>
              {t('family.purchase')}
            </Core.Link>

            <TrialFontsButton />
          </div>
        )}
      </div>
    </ColorBar>
  );
};

export default FamiliesHero;
